import { useEffect, useMemo, useRef, useState, type ReactNode, type MouseEvent } from 'react'
import { useDrag } from '@/hooks/useDrag'
import DraggableHeader from './FloatingPanel/DraggableHeader'
import { useUiFlags } from '@/contexts/UiFlagsContext'

interface FloatingPanelProps {
	x: number
	y: number
	bounds?: { width: number; height: number }
	onClose?: () => void
	children: ReactNode | ((dragProps: { onDragStart: (e: MouseEvent) => void; showTrace: boolean }) => ReactNode)
	title?: string
	sourceFile?: string
	zIndexClass?: string // Tailwind z-index utility e.g., 'z-30', 'z-40', 'z-50'
	getContainerRect?: () => { left: number; top: number }
	minimal?: boolean // If true, skip debug styling/headers even if showTrace is on
}

export default function FloatingPanel({ x, y, bounds, onClose, children, title, sourceFile, zIndexClass = 'z-40', getContainerRect, minimal = false }: FloatingPanelProps) {
	const ref = useRef<HTMLDivElement>(null)
	const [size, setSize] = useState<{ width: number; height: number }>()
	const { showTrace } = useUiFlags()

	useEffect(() => {
		if (!ref.current) return
		const w = ref.current.offsetWidth || 260
		const h = ref.current.offsetHeight || 150
		setSize({ width: w, height: h })
	}, [])

	// Keep size in sync when content changes (so bounds clamping stays correct)
	useEffect(() => {
		if (!ref.current || typeof ResizeObserver === 'undefined') return
		const ro = new ResizeObserver((entries) => {
			const el = entries[0]?.target as HTMLDivElement
			if (!el) return
			setSize({ width: el.offsetWidth || 260, height: el.offsetHeight || 150 })
		})
		ro.observe(ref.current)
		return () => ro.disconnect()
	}, [])

	const { pos, setPos, start } = useDrag({ x, y }, bounds, size, getContainerRect)

	useEffect(() => setPos({ x, y }), [x, y, setPos])

	// Render children - support both ReactNode and render prop patterns
	const renderChildren = () => {
		if (typeof children === 'function') {
			return children({ onDragStart: start, showTrace: showTrace && !minimal })
		}
		return children
	}

	const isTraceEnabled = showTrace && !minimal

	return (
		<div
			ref={ref}
			className={`absolute ${zIndexClass} nowheel ${isTraceEnabled
					? 'min-w-[260px] max-w-[360px] bg-neutral-900/95 border border-neutral-800 rounded-xl shadow-lg p-2'
					: 'pointer-events-auto'
				}`}
			style={{ left: pos.x, top: pos.y }}
		>
			{/* Show full header with title only when trace mode is on */}
			{isTraceEnabled && (
				<DraggableHeader title={title} onMouseDown={start} onClose={onClose} />
			)}
			<div className={!isTraceEnabled ? "contents" : ""}>{renderChildren()}</div>
			{isTraceEnabled && sourceFile && (
				<div className="mt-2 pt-1.5 border-t border-neutral-800" onMouseDown={(e) => e.stopPropagation()}>
					<div className="w-full flex flex-col items-end gap-1">
						<span className="text-[10px] text-neutral-300 bg-neutral-800/80 border border-neutral-700 rounded px-2 py-[3px] leading-none truncate max-w-full">
							{sourceFile}
						</span>
						<span className="text-[10px] text-neutral-400">
							z-index: {zIndexClass}
						</span>
					</div>
				</div>
			)}
		</div>
	)
}